import React, { useState } from 'react';
import config from './config';

const FTOProPage = ({ user, onBack, setPage, onUsageUpdate }) => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [progress, setProgress] = useState({ percent: 0, message: '' });
  const [formData, setFormData] = useState({
    productName: '',
    description: '',
    keyFeatures: '',
    targetMarket: 'United States'
  });
  const [results, setResults] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [expandedSections, setExpandedSections] = useState({});

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const getDummyResults = () => ({
    success: true,
    report_date: new Date().toISOString().split('T')[0],
    product_name: formData.productName,
    target_market: formData.targetMarket,
    executive_summary: {
      overall_risk: 'MEDIUM-HIGH',
      go_no_go: 'CONDITIONAL GO',
      headline: 'Proceed with caution - design modifications recommended before market entry',
      key_findings: [
        'Two HIGH-risk patents identified requiring immediate attention (Enphase microinverter, SolarEdge MPPT)',
        'Flush-mount aesthetic design appears clear - no blocking patents found',
        'AI energy management system has freedom to operate with minor modifications',
        'Recommend design-around for per-panel power conversion to avoid Enphase claims',
        'Three academic papers identified that may support invalidity arguments'
      ],
      estimated_clearance_cost: '$15,000 - $25,000',
      timeline_impact: '6-8 weeks for design modifications'
    },
    heat_map: {
      features: [
        { name: 'F1: Flush-mount roof integration', risk: 'LOW', notes: 'Design patents expired; utility patents narrowly scoped' },
        { name: 'F2: Per-panel microinverter', risk: 'HIGH', notes: 'Enphase holds broad claims on distributed power conversion' },
        { name: 'F3: MPPT optimization', risk: 'HIGH', notes: 'SolarEdge patent family covers adaptive algorithms' },
        { name: 'F4: AI load balancing', risk: 'MEDIUM', notes: 'Some overlap with Tesla Autobidder concepts' },
        { name: 'F5: Battery integration', risk: 'LOW', notes: 'Standard BMS architecture; no blocking patents' },
        { name: 'F6: Grid-tie inverter', risk: 'MEDIUM', notes: 'Crowded space but viable design-around exists' },
        { name: 'F7: Mobile app control', risk: 'LOW', notes: 'UI/UX patents weak; API integration clear' }
      ]
    },
    high_risk_patents: [
      {
        patent_number: '10,651,787',
        title: 'Distributed Power Harvesting Systems Using DC Power Sources',
        assignee: 'Enphase Energy, Inc.',
        filing_date: '2018-03-15',
        expiry_date: '2038-03-15',
        status: 'ACTIVE',
        relevance_score: 92,
        key_claims: ['Claim 1: per-panel power conversion module', 'Claim 4: distributed MPPT architecture'],
        overlap_analysis: 'Direct overlap with F2 (microinverter) feature. Claim 1 covers the core architecture of per-panel DC-AC conversion with integrated MPPT.',
        doe_sensitivity: 'HIGH - Doctrine of Equivalents likely applies to alternative power conversion topologies',
        design_around_difficulty: 'MODERATE - String inverter alternative available but reduces efficiency',
        counsel_note: 'Priority review recommended. Consider licensing or design modification.'
      },
      {
        patent_number: '11,081,608',
        title: 'Techniques for Maximizing Solar Panel Power Output',
        assignee: 'SolarEdge Technologies Ltd.',
        filing_date: '2019-06-22',
        expiry_date: '2039-06-22',
        status: 'ACTIVE',
        relevance_score: 88,
        key_claims: ['Claim 1: adaptive power point tracking', 'Claim 7: machine learning optimization'],
        overlap_analysis: 'Overlaps with F3 (MPPT optimization). Claims cover adaptive algorithms that adjust tracking based on environmental conditions.',
        doe_sensitivity: 'MEDIUM - Specific algorithm implementation may provide clearance',
        design_around_difficulty: 'LOW-MODERATE - Fixed-ratio MPPT avoids claims but sacrifices 2-3% efficiency',
        counsel_note: 'Review claim construction carefully. May have prosecution history estoppel.'
      },
      {
        patent_number: '10,886,746',
        title: 'Module-Level Power Electronics with Safety Features',
        assignee: 'Enphase Energy, Inc.',
        filing_date: '2017-08-20',
        expiry_date: '2037-08-20',
        status: 'ACTIVE',
        relevance_score: 85,
        key_claims: ['Claim 1: rapid shutdown compliance', 'Claim 3: module-level monitoring'],
        overlap_analysis: 'Overlaps with F2 safety and monitoring features. NEC 2017 rapid shutdown requirements may be covered.',
        doe_sensitivity: 'MEDIUM - Alternative shutdown methods exist',
        design_around_difficulty: 'LOW - Use third-party certified shutdown devices',
        counsel_note: 'Consider using pre-licensed shutdown components.'
      },
      {
        patent_number: '11,108,352',
        title: 'Photovoltaic System Impedance Matching',
        assignee: 'Enphase Energy, Inc.',
        filing_date: '2019-11-05',
        expiry_date: '2039-11-05',
        status: 'ACTIVE',
        relevance_score: 79,
        key_claims: ['Claim 1: dynamic impedance matching', 'Claim 5: panel string optimization'],
        overlap_analysis: 'Partial overlap with F3 optimization. Claims focused on impedance matching specifically.',
        doe_sensitivity: 'LOW - Claims narrowly construed',
        design_around_difficulty: 'LOW - Use fixed impedance matching',
        counsel_note: 'Lower priority. Monitor for continuations.'
      }
    ],
    medium_risk_patents: [
      {
        patent_number: '10,985,571',
        title: 'Energy Storage System with Predictive Load Management',
        assignee: 'Tesla, Inc.',
        filing_date: '2017-09-10',
        expiry_date: '2037-09-10',
        status: 'ACTIVE',
        relevance_score: 71,
        key_claims: ['Claim 3: predictive load balancing using historical data'],
        overlap_analysis: 'Partial overlap with F4 (AI load balancing). Claims are limited to specific prediction methodology.',
        doe_sensitivity: 'LOW - Claims narrowly construed in prosecution',
        design_around_difficulty: 'LOW - Rule-based system avoids AI prediction claims',
        counsel_note: 'Lower priority. Design-around straightforward.'
      },
      {
        patent_number: '11,201,565',
        title: 'Bidirectional Power Flow Control for Grid-Connected Systems',
        assignee: 'Sunrun Inc.',
        filing_date: '2020-01-15',
        expiry_date: '2040-01-15',
        status: 'ACTIVE',
        relevance_score: 65,
        key_claims: ['Claim 1: seamless grid/battery transition', 'Claim 5: anti-islanding detection'],
        overlap_analysis: 'Overlaps with F6 (grid-tie inverter). Standard anti-islanding may be covered.',
        doe_sensitivity: 'MEDIUM',
        design_around_difficulty: 'LOW - Alternative anti-islanding methods available',
        counsel_note: 'Standard licensing terms typically available from Sunrun.'
      },
      {
        patent_number: '10,756,543',
        title: 'Solar Panel Mounting System with Integrated Wiring',
        assignee: 'SunPower Corporation',
        filing_date: '2016-11-20',
        expiry_date: '2036-11-20',
        status: 'ACTIVE',
        relevance_score: 58,
        key_claims: ['Claim 2: concealed wiring channel in mounting rail'],
        overlap_analysis: 'Minor overlap with F1 (flush-mount). Claims limited to specific rail design.',
        doe_sensitivity: 'LOW',
        design_around_difficulty: 'LOW - Alternative mounting approaches available',
        counsel_note: 'Low concern. Multiple design-around options exist.'
      },
      {
        patent_number: '11,349,419',
        title: 'Residential Energy Arbitrage Optimization',
        assignee: 'Generac Power Systems',
        filing_date: '2021-03-08',
        expiry_date: '2041-03-08',
        status: 'ACTIVE',
        relevance_score: 52,
        key_claims: ['Claim 1: time-of-use rate optimization'],
        overlap_analysis: 'Overlaps with F4 if implementing utility rate arbitrage.',
        doe_sensitivity: 'MEDIUM',
        design_around_difficulty: 'LOW - Avoid TOU optimization or use different algorithm',
        counsel_note: 'Only relevant if implementing rate arbitrage feature.'
      },
      {
        patent_number: '10,892,617',
        title: 'Smart Inverter Communication Protocol',
        assignee: 'Fronius International GmbH',
        filing_date: '2018-07-30',
        expiry_date: '2038-07-30',
        status: 'ACTIVE',
        relevance_score: 48,
        key_claims: ['Claim 4: inverter-to-cloud data protocol'],
        overlap_analysis: 'May overlap with F7 (mobile app) if using similar protocol.',
        doe_sensitivity: 'LOW',
        design_around_difficulty: 'LOW - Use standard protocols (Modbus, SunSpec)',
        counsel_note: 'Use industry-standard protocols to avoid.'
      }
    ],
    low_risk_patents: [
      { patent_number: '10,523,150', title: 'Aesthetic Solar Panel Frame Design', assignee: 'CertainTeed LLC', expiry_date: '2035-04-12', relevance_score: 35, counsel_note: 'Design patent only. No action required.' },
      { patent_number: '11,056,990', title: 'Solar Panel Cleaning Robot', assignee: 'Ecoppia Scientific Ltd.', expiry_date: '2039-02-28', relevance_score: 22, counsel_note: 'Not relevant - no cleaning feature.' },
      { patent_number: '10,673,373', title: 'Rapid Shutdown System for PV Arrays', assignee: 'Tigo Energy, Inc.', expiry_date: '2037-05-18', relevance_score: 41, counsel_note: 'Use UL-certified devices to avoid.' },
      { patent_number: '11,190,126', title: 'Weather-Based Solar Production Forecasting', assignee: 'IBM Corporation', expiry_date: '2040-08-05', relevance_score: 38, counsel_note: 'Different ML architecture - no action.' },
      { patent_number: '10,411,644', title: 'Integrated Solar and HVAC Control', assignee: 'Honeywell International', expiry_date: '2036-09-22', relevance_score: 28, counsel_note: 'Only if adding HVAC integration.' },
      { patent_number: '11,424,616', title: 'Peer-to-Peer Energy Trading Platform', assignee: 'LO3 Energy Inc.', expiry_date: '2041-06-14', relevance_score: 19, counsel_note: 'No P2P trading - not applicable.' }
    ],academic_papers: [
      {
        title: 'Distributed Maximum Power Point Tracking for Photovoltaic Systems: A Comparative Analysis',
        authors: 'Chen, Wei et al.',
        venue: 'IEEE Transactions on Power Electronics',
        year: 2016,
        doi: '10.1109/TPEL.2016.2521345',
        relevance: 'Describes distributed MPPT techniques predating Enphase claims. Potential invalidity argument for US10,651,787.',
        publication_status: 'BEFORE_FILING',
        cited_by_count: 245
      },
      {
        title: 'Machine Learning Approaches for Solar Irradiance Forecasting',
        authors: 'Rodriguez, Maria & Smith, John',
        venue: 'Renewable Energy Journal',
        year: 2015,
        doi: '10.1016/j.renene.2015.08.042',
        relevance: 'Prior art for AI-based optimization claims. May invalidate SolarEdge ML claims in US11,081,608.',
        publication_status: 'BEFORE_FILING',
        cited_by_count: 189
      },
      {
        title: 'Residential Battery Storage Economics Under Time-of-Use Pricing',
        authors: 'Johnson, Robert et al.',
        venue: 'Applied Energy',
        year: 2017,
        doi: '10.1016/j.apenergy.2017.03.044',
        relevance: 'Documents TOU optimization strategies predating Generac patent. Validity challenge potential.',
        publication_status: 'BEFORE_FILING',
        cited_by_count: 156
      }
    ],
    competitor_archetypes: [
      {
        name: 'Enphase Energy',
        strategy: 'Microinverter pioneer with strong IP moat. Actively enforces patents against competitors.',
        patent_count: 523,
        risk_profile: 'HIGH - Core technology directly overlaps with product',
        key_patents: ['US10,651,787', 'US10,886,746', 'US11,108,352'],
        licensing_history: 'Has sued SolarEdge, Huawei. Licensing available but expensive ($15-25/unit).'
      },
      {
        name: 'SolarEdge Technologies',
        strategy: 'Power optimizer focus with strong MPPT portfolio. Cross-licensed with some competitors.',
        patent_count: 412,
        risk_profile: 'HIGH - MPPT optimization patents broadly scoped',
        key_patents: ['US11,081,608', 'US10,756,699'],
        licensing_history: 'Settled with Huawei (2019). Open to licensing for smaller players.'
      },
      {
        name: 'Tesla Energy (Powerwall/Solar Roof)',
        strategy: 'Integrated ecosystem with proprietary hardware and software. Aggressive portfolio but focused on vehicle-to-grid.',
        patent_count: 847,
        risk_profile: 'MEDIUM - Strong in energy storage, weaker in panel-level optimization',
        key_patents: ['US10,985,571', 'US11,267,338'],
        licensing_history: 'Open patent pledge for good faith actors (2014). Rarely enforces against residential solar.'
      }
    ],
    design_around_options: [
      {
        id: 'DA-1',
        target_patent: 'US10,651,787 (Enphase)',
        strategy: 'Hybrid String/Module Architecture',
        description: 'Use string inverter with module-level power optimizers instead of full microinverters. Optimizers handle MPPT but AC conversion happens at string level.',
        implementation_effort: 'MEDIUM',
        performance_impact: 'Minimal (less than 2% efficiency loss)',
        residual_risk: 'MEDIUM - May still trigger Doctrine of Equivalents',
        timeline: '6-8 weeks',
        cost_estimate: '$8,000 - $12,000',
        recommendation: 'PREFERRED'
      },
      {
        id: 'DA-2',
        target_patent: 'US11,081,608 (SolarEdge)',
        strategy: 'Fixed-Ratio MPPT',
        description: 'Implement MPPT with fixed voltage ratios rather than adaptive ML-based optimization. Sacrifices some efficiency but clearly avoids claims.',
        implementation_effort: 'LOW',
        performance_impact: 'Moderate (2-3% efficiency loss)',
        residual_risk: 'LOW',
        timeline: '3-4 weeks',
        cost_estimate: '$3,000 - $5,000',
        recommendation: 'ACCEPTABLE'
      },
      {
        id: 'DA-3',
        target_patent: 'US10,985,571 (Tesla)',
        strategy: 'Rule-Based Load Management',
        description: 'Replace AI prediction with deterministic rule-based system. Uses time schedules and thresholds instead of ML.',
        implementation_effort: 'LOW',
        performance_impact: 'Minor (less optimal arbitrage)',
        residual_risk: 'LOW',
        timeline: '2-3 weeks',
        cost_estimate: '$2,000 - $4,000',
        recommendation: 'PREFERRED'
      },
      {
        id: 'DA-4',
        target_patent: 'Multiple grid-tie patents',
        strategy: 'Licensed Inverter Module',
        description: 'Use pre-licensed inverter from established manufacturer (e.g., Fronius, SMA) rather than custom design.',
        implementation_effort: 'LOW',
        performance_impact: 'None',
        residual_risk: 'LOW',
        timeline: '1-2 weeks',
        cost_estimate: '$5,000 - $8,000 (licensing premium)',
        recommendation: 'PREFERRED'
      }
    ],
    attorney_brief: {
      priority_actions: [
        'Review Enphase US10,651,787 claim construction - potential design-around via hybrid architecture',
        'Obtain SolarEdge prosecution history for US11,081,608 - look for limiting amendments',
        'Confirm Tesla open patent pledge applicability to residential products'
      ],
      claim_chart_needed: ['US10,651,787 (Enphase)', 'US11,081,608 (SolarEdge)'],
      opinion_letter_scope: 'Non-infringement opinion for modified microinverter architecture; invalidity opinion for MPPT claims based on Chen 2016 prior art',
      estimated_legal_cost: '$12,000 - $18,000'
    },
    verification_status: {
      patent_grant_status: { status: 'verified', source: 'PatTrak database', confidence: 'High' },
      patent_expiry_dates: { status: 'calculated', source: 'Filing date + 20 years', confidence: 'High' },
      assignee_current: { status: 'as_filed', source: 'PatTrak database', confidence: 'Medium' },
      family_continuations: { status: 'not_reviewed', source: 'Counsel action required', confidence: null },
      prosecution_history: { status: 'not_reviewed', source: 'Counsel action required', confidence: null },
      litigation_history: { status: 'not_reviewed', source: 'Counsel action required', confidence: null },
      maintenance_status: { status: 'not_reviewed', source: 'Counsel action required', confidence: null }
    },
    database_limitations: {
      patents_included: '1.45 million USPTO grants',
      date_range: '2021-2024',
      applications_included: false,
      non_us_patents: false,
      impact_notes: [
        'Older foundational patents (pre-2021) may not appear in results',
        'Recently published applications are not assessed',
        'Non-US equivalents require separate jurisdiction analysis'
      ]
    },
    conclusions: {
      overall_assessment: 'The SolarRoof Pro product faces moderate-to-high patent risk, primarily from Enphase and SolarEdge portfolios. However, viable design-around strategies exist that would reduce risk to acceptable levels with 6-8 weeks of engineering effort.',
      go_no_go: 'CONDITIONAL GO',
      conditions: [
        'Implement hybrid string/module architecture (DA-1) before commercial launch',
        'Obtain freedom-to-operate opinion letter for modified design',
        'Budget for potential Enphase licensing negotiations as contingency'
      ],
      escalation_triggers: [
        'Enphase files new continuation with broader claims',
        'SolarEdge initiates licensing discussions',
        'New blocking patent identified in target CPC classes'
      ]
    },
    change_triggers: [
      'Product design changes affecting any of features F1-F7',
      'New patent grants in CPC classes H02S, H02J, H02M',
      'Identified patents expire, are invalidated, or change ownership',
      'Expansion to new geographic markets (EU, Asia-Pacific)',
      'Competitor litigation or licensing activity',
      'Annual review cycle (recommend every 12 months)'
    ],
    search_methodology: {
      cpc_classes: ['H02S 40/00', 'H02J 3/38', 'H02J 7/35', 'H02M 7/42', 'G05F 1/67'],
      keywords: ['solar', 'photovoltaic', 'microinverter', 'MPPT', 'power optimizer', 'energy storage', 'grid-tie'],
      assignee_focus: ['Enphase', 'SolarEdge', 'Tesla', 'Sunrun', 'SunPower'],
      patents_reviewed: 847,
      shortlisted: 15
    }
  });

  const handleSubmit = async () => {
    if (!formData.productName.trim() || !formData.description.trim()) {
      setError('Please fill in at least the product name and description');
      return;
    }
    setLoading(true);
    setError('');
    setStep(2);
    setProgress({ percent: 5, message: 'Initializing FTO analysis...' });

    const progressUpdates = [
      { percent: 10, message: 'Parsing product features...' },
      { percent: 20, message: 'Identifying relevant CPC classes...' },
      { percent: 35, message: 'Searching USPTO database (1.45M patents)...' },
      { percent: 50, message: 'Analyzing claim scope and overlap...' },
      { percent: 65, message: 'Assessing Doctrine of Equivalents exposure...' },
      { percent: 75, message: 'Evaluating design-around options...' },
      { percent: 85, message: 'Generating risk assessment...' },
      { percent: 95, message: 'Preparing counsel-grade report...' }
    ];
    let idx = 0;
    const interval = setInterval(() => {
      if (idx < progressUpdates.length) {
        setProgress(progressUpdates[idx]);
        idx++;
      }
    }, 2000);

    try {
      const response = await fetch(`${config.API_URL}/api/fto-pro-analyze`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          productName: formData.productName,
          description: formData.description,
          keyFeatures: formData.keyFeatures,
          targetMarket: formData.targetMarket
        })
      });

      clearInterval(interval);

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Analysis failed');
      }

      const data = await response.json();
      
      if (data.success) {
        setResults(data);
        setStep(3);
        setExpandedSections({ executive: true, heatmap: true, 'high-risk': true });
        if (onUsageUpdate) onUsageUpdate();
      } else {
        throw new Error(data.error || 'Analysis failed');
      }
    } catch (err) {
      clearInterval(interval);
      console.error('FTO Analysis error:', err);
      setError(err.message || 'Error performing FTO analysis. Please try again.');
      setStep(1);
    } finally {
      setLoading(false);
    }
  };

  const resetAnalysis = () => {
    setStep(1);
    setFormData({ productName: '', description: '', keyFeatures: '', targetMarket: 'United States' });
    setResults(null);
    setExpandedSections({});
  };

  const saveReport = async () => {
    const token = localStorage.getItem('token');
    if (!token) { alert('Please log in to save reports'); return; }
    setSaving(true);
    try {
      const response = await fetch(`${config.API_URL}/api/saved-individual-reports`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          report_type: 'fto-pro',
          title: formData.productName || 'FTO Pro Report',
          input_data: formData,
          results_data: results,
          token
        })
      });
      const data = await response.json();
      if (data.success) {
        setSaveSuccess(true);
        setTimeout(() => setSaveSuccess(false), 3000);
      } else {
        alert(data.error || 'Failed to save report');
      }
    } catch (err) {
      alert('Error saving report');
    } finally {
      setSaving(false);
    }
  };

const downloadPDF = async () => {
    if (!results) return;
    try {
      const response = await fetch(`${config.API_URL}/api/fto-pro-pdf`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ form_data: formData, results: results })
      });
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `FTO_Pro_${(formData.productName || 'Report').replace(/[^a-z0-9]/gi, '_')}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      } else {
        alert('PDF generation failed. Try HTML download.');
      }
    } catch (error) {
      alert('PDF generation failed. Try HTML download.');
    }
  };

  const downloadHTML = () => {
    if (!results) return;
    const r = results;
    const html = `<!DOCTYPE html>
<html><head><title>FTO Pro Report: ${formData.productName}</title>
<style>
body{font-family:Arial,sans-serif;max-width:1000px;margin:0 auto;padding:40px;line-height:1.6;color:#1e293b}
h1{color:#0369a1;border-bottom:3px solid #0369a1;padding-bottom:10px}
h2{color:#0369a1;margin-top:30px;border-bottom:2px solid #e2e8f0;padding-bottom:8px}
.disclaimer{background:#fef3c7;border-left:4px solid #f59e0b;padding:16px;margin:20px 0}
.risk-banner{padding:24px;border-radius:12px;margin:24px 0}
table{width:100%;border-collapse:collapse;margin:16px 0}
th,td{border:1px solid #e2e8f0;padding:12px;text-align:left}
th{background:#f1f5f9}
.patent-card{border-left:4px solid;padding:16px;margin:12px 0;border-radius:0 8px 8px 0}
.high{border-color:#dc2626;background:#fef2f2}
.medium{border-color:#d97706;background:#fffbeb}
.low{border-color:#16a34a;background:#f0fdf4}
.da-card{background:#eff6ff;border:1px solid #bfdbfe;padding:16px;margin:12px 0;border-radius:8px}
</style></head><body>
<h1>Freedom to Operate (FTO) Pro Report</h1>
<p><strong>Product:</strong> ${formData.productName}<br><strong>Target Market:</strong> ${formData.targetMarket}<br><strong>Date:</strong> ${r.report_date}</p>
<div class="disclaimer"><strong>DISCLAIMER:</strong> This AI-assisted analysis is for informational purposes only and does not constitute legal advice. Review with qualified patent counsel before making business decisions.</div>
<div class="risk-banner" style="background:#fef3c7;border:2px solid #d97706">
<div style="font-size:24px;font-weight:bold;color:#92400e">Overall Risk: ${r.executive_summary.overall_risk}</div>
<div style="display:inline-block;background:#d97706;color:white;padding:8px 20px;border-radius:20px;margin-top:12px">${r.executive_summary.go_no_go}</div>
<p style="margin-top:16px;color:#92400e">${r.executive_summary.headline}</p></div>

<h2>1. Executive Summary</h2>
<h3>Key Findings</h3><ul>${r.executive_summary.key_findings.map(f=>'<li>'+f+'</li>').join('')}</ul>
<p><strong>Estimated Clearance Cost:</strong> ${r.executive_summary.estimated_clearance_cost}<br>
<strong>Timeline Impact:</strong> ${r.executive_summary.timeline_impact}</p>

<h2>2. Feature Risk Heat Map</h2>
<table><tr><th>Feature</th><th>Risk</th><th>Notes</th></tr>
${r.heat_map.features.map(f=>'<tr><td>'+f.name+'</td><td>'+f.risk+'</td><td>'+f.notes+'</td></tr>').join('')}</table>

<h2>3. HIGH Risk Patents (${r.high_risk_patents.length})</h2>
${r.high_risk_patents.map(p=>'<div class="patent-card high"><strong>US'+p.patent_number+'</strong>: '+p.title+'<br><small>Assignee: '+p.assignee+' | Expires: '+p.expiry_date+' | Relevance: '+p.relevance_score+'%</small><p><strong>Overlap:</strong> '+p.overlap_analysis+'</p><p><strong>DoE Sensitivity:</strong> '+p.doe_sensitivity+'</p><p><strong>Counsel Note:</strong> '+p.counsel_note+'</p></div>').join('')}

<h2>4. MEDIUM Risk Patents (${r.medium_risk_patents.length})</h2>
${r.medium_risk_patents.map(p=>'<div class="patent-card medium"><strong>US'+p.patent_number+'</strong>: '+p.title+'<br><small>Assignee: '+p.assignee+' | Relevance: '+p.relevance_score+'%</small><p>'+p.overlap_analysis+'</p></div>').join('')}

<h2>5. LOW Risk Patents (${r.low_risk_patents.length})</h2>
${r.low_risk_patents.map(p=>'<div class="patent-card low"><strong>US'+p.patent_number+'</strong>: '+p.title+' - '+p.counsel_note+'</div>').join('')}

<h2>6. Design-Around Strategies</h2>
${r.design_around_options.map(d=>'<div class="da-card"><strong>'+d.id+': '+d.strategy+'</strong> ['+d.recommendation+']<br><small>Target: '+d.target_patent+'</small><p>'+d.description+'</p><p>Timeline: '+d.timeline+' | Cost: '+d.cost_estimate+' | Effort: '+d.implementation_effort+'</p></div>').join('')}

<h2>7. Prior Art (Validity Challenges)</h2>
<p><em>Academic papers that may support invalidity arguments. Note: Validity analysis is separate from infringement.</em></p>
${r.academic_papers.map(p=>'<div style="border:1px solid #e2e8f0;padding:12px;margin:8px 0;border-radius:8px"><strong>'+p.title+'</strong><br><small>'+p.authors+' | '+p.venue+' ('+p.year+') | '+p.cited_by_count+' citations</small><p>'+p.relevance+'</p></div>').join('')}

<h2>8. Competitor Analysis</h2>
${r.competitor_archetypes.map(c=>'<div style="border:1px solid #e2e8f0;padding:16px;margin:12px 0;border-radius:8px"><strong>'+c.name+'</strong> - '+c.risk_profile+'<p>'+c.strategy+'</p><small>Patents: '+c.patent_count+' | Key: '+c.key_patents.join(', ')+'</small><p><em>Licensing: '+c.licensing_history+'</em></p></div>').join('')}

<h2>9. Attorney Brief</h2>
<h3>Priority Actions</h3><ol>${r.attorney_brief.priority_actions.map(a=>'<li>'+a+'</li>').join('')}</ol>
<p><strong>Claim Charts Needed:</strong> ${r.attorney_brief.claim_chart_needed.join(', ')}<br>
<strong>Opinion Letter Scope:</strong> ${r.attorney_brief.opinion_letter_scope}<br>
<strong>Estimated Legal Cost:</strong> ${r.attorney_brief.estimated_legal_cost}</p>

<h2>10. Conclusions</h2>
<p>${r.conclusions.overall_assessment}</p>
<h3>Conditions for Proceeding</h3><ul>${r.conclusions.conditions.map(c=>'<li>'+c+'</li>').join('')}</ul>
<h3>Escalation Triggers</h3><ul>${r.conclusions.escalation_triggers.map(t=>'<li>'+t+'</li>').join('')}</ul>
<h3>Change Triggers</h3><ul>${r.change_triggers.map(t=>'<li>'+t+'</li>').join('')}</ul>

<h2>11. Verification Status</h2>
<table><tr><th>Item</th><th>Status</th><th>Source</th><th>Confidence</th></tr>
${Object.entries(r.verification_status).map(([k,v])=>'<tr><td>'+k.replace(/_/g,' ')+'</td><td>'+v.status+'</td><td>'+v.source+'</td><td>'+(v.confidence||'-')+'</td></tr>').join('')}</table>

<h2>12. Database Limitations</h2>
<ul><li><strong>Patents:</strong> ${r.database_limitations.patents_included}</li>
<li><strong>Date Range:</strong> ${r.database_limitations.date_range}</li>
<li><strong>Applications:</strong> ${r.database_limitations.applications_included?'Included':'Not included'}</li>
<li><strong>Non-US:</strong> ${r.database_limitations.non_us_patents?'Included':'Not included'}</li></ul>
<p><strong>Impact:</strong></p><ul>${r.database_limitations.impact_notes.map(n=>'<li>'+n+'</li>').join('')}</ul>

<div style="margin-top:60px;border-top:2px solid #e2e8f0;padding-top:20px;color:#64748b;font-size:12px">
<p>Generated by PatTrak FTO Pro - ${new Date().toLocaleDateString()}</p>
<p>This report is for informational purposes only and does not constitute legal advice.</p></div>
</body></html>`;
    const blob = new Blob([html], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `FTO_Pro_Report_${formData.productName.replace(/[^a-z0-9]/gi, '_')}.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const getRiskColor = (risk) => {
    if (risk === 'HIGH') return { bg: '#fee2e2', border: '#dc2626', text: '#991b1b' };
    if (risk === 'MEDIUM' || risk === 'MEDIUM-HIGH') return { bg: '#fef3c7', border: '#d97706', text: '#92400e' };
    return { bg: '#dcfce7', border: '#16a34a', text: '#166534' };
  };

  // PRO TIER GATE
  if (!user || user.tier !== 'pro') {
    return (
      <div style={{ minHeight: '100vh', background: '#f8fafc' }}>
        <header style={{ padding: '16px' }}>
          <button onClick={onBack} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#0369a1' }}>← Back</button>
        </header>
        <main style={{ maxWidth: '500px', margin: '80px auto', textAlign: 'center' }}>
          <div style={{ background: 'white', borderRadius: '16px', padding: '48px', boxShadow: '0 4px 6px rgba(0,0,0,0.05)' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>⚖️</div>
            <h1 style={{ fontSize: '24px', color: '#1e293b', marginBottom: '16px' }}>FTO Pro Analysis</h1>
            <p style={{ color: '#64748b', marginBottom: '24px' }}>Professional Freedom to Operate analysis with counsel-grade reporting.</p>
            <p style={{ background: '#fef3c7', padding: '12px', borderRadius: '8px', color: '#92400e', marginBottom: '24px' }}>
              {!user ? 'Please log in to access FTO Pro.' : 'FTO Pro requires a Pro subscription.'}
            </p>
            {!user ? (
              <button onClick={() => setPage('login')} style={{ padding: '12px 24px', background: '#0369a1', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer' }}>Log In</button>
            ) : (
              <button onClick={() => setPage('pricing')} style={{ padding: '12px 24px', background: '#0369a1', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer' }}>Upgrade to Pro</button>
            )}
          </div>
        </main>
      </div>
    );
  }
// MAIN RENDER
  return (
    <div style={{ maxWidth: '1000px', margin: '0 auto', padding: '24px' }}>
      <button onClick={onBack} style={{ background: 'none', border: 'none', color: '#0369a1', cursor: 'pointer', marginBottom: '16px' }}>← Back to Search</button>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
        <h1 style={{ fontSize: '28px', fontWeight: '700', color: '#1e293b', margin: 0 }}>FTO Pro Analysis</h1>
        <span style={{ background: '#0369a1', color: 'white', padding: '4px 12px', borderRadius: '12px', fontSize: '12px' }}>PRO</span>
      </div>
      <p style={{ color: '#64748b', marginBottom: '24px' }}>Professional Freedom to Operate analysis with counsel-grade reporting</p>

      {/* Progress Steps */}
      <div style={{ display: 'flex', marginBottom: '32px', padding: '16px', background: '#f8fafc', borderRadius: '8px' }}>
        {['Define Product', 'Analyze', 'Report'].map((label, i) => (
          <React.Fragment key={i}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div style={{ width: '28px', height: '28px', borderRadius: '50%', background: step > i ? '#0369a1' : '#e2e8f0', color: step > i ? 'white' : '#64748b', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', fontWeight: '600' }}>{step > i + 1 ? '✓' : i + 1}</div>
              <span style={{ color: step > i ? '#0369a1' : '#64748b', fontSize: '14px' }}>{label}</span>
            </div>
            {i < 2 && <div style={{ flex: 1, height: '2px', background: step > i + 1 ? '#0369a1' : '#e2e8f0', margin: '0 12px' }} />}
          </React.Fragment>
        ))}
      </div>

      {error && <div style={{ padding: '12px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', color: '#dc2626', marginBottom: '24px' }}>{error}</div>}

      {/* STEP 1: Input Form */}
      {step === 1 && (
        <div style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '24px' }}>
          <div style={{ background: '#eff6ff', padding: '16px', borderRadius: '8px', marginBottom: '24px' }}>
            <strong style={{ color: '#1e40af' }}>What is Freedom to Operate (FTO)?</strong>
            <p style={{ margin: '8px 0 0', color: '#1e40af', fontSize: '14px' }}>FTO analysis determines whether your product can be commercialized without infringing active patents. This tool identifies blocking patents, assesses risk levels, and suggests design-around strategies.</p>
          </div>
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600' }}>Product/Technology Name *</label>
            <input type="text" value={formData.productName} onChange={e => handleInputChange('productName', e.target.value)} placeholder="e.g., SolarRoof Pro, SmartValve Controller" style={{ width: '100%', padding: '12px', border: '1px solid #d1d5db', borderRadius: '8px', boxSizing: 'border-box' }} />
          </div>
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600' }}>Detailed Technical Description *</label>
            <textarea value={formData.description} onChange={e => handleInputChange('description', e.target.value)} placeholder="Describe your product in detail. Include: what it does, how it works, key technical components, and what makes it unique..." rows={5} style={{ width: '100%', padding: '12px', border: '1px solid #d1d5db', borderRadius: '8px', boxSizing: 'border-box' }} />
          </div>
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600' }}>Key Technical Features</label>
            <textarea value={formData.keyFeatures} onChange={e => handleInputChange('keyFeatures', e.target.value)} placeholder="List key features (one per line):&#10;• Flush-mount solar panel integration&#10;• Per-panel microinverter with MPPT&#10;• AI-based energy load balancing" rows={4} style={{ width: '100%', padding: '12px', border: '1px solid #d1d5db', borderRadius: '8px', boxSizing: 'border-box' }} />
          </div>
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600' }}>Target Market</label>
            <select value={formData.targetMarket} onChange={e => handleInputChange('targetMarket', e.target.value)} style={{ width: '100%', padding: '12px', border: '1px solid #d1d5db', borderRadius: '8px', background: 'white' }}>
              <option value="United States">United States</option>
              <option value="European Union">European Union</option>
              <option value="United Kingdom">United Kingdom</option>
              <option value="Global">Global (All Major Markets)</option>
            </select>
            <p style={{ margin: '4px 0 0', fontSize: '12px', color: '#64748b' }}>Note: Current database covers USPTO patents only.</p>
          </div>
          <div style={{ background: '#fef3c7', padding: '12px', borderRadius: '8px', marginBottom: '24px', fontSize: '14px', color: '#92400e' }}>
            <strong>Important:</strong> This AI-assisted analysis is for informational purposes only. Always consult qualified patent counsel before making commercialization decisions.
          </div>
          <button onClick={handleSubmit} disabled={!formData.productName.trim() || !formData.description.trim()} style={{ width: '100%', padding: '14px', background: formData.productName.trim() && formData.description.trim() ? '#0369a1' : '#94a3b8', color: 'white', border: 'none', borderRadius: '8px', fontSize: '16px', fontWeight: '600', cursor: formData.productName.trim() && formData.description.trim() ? 'pointer' : 'not-allowed' }}>Start FTO Analysis →</button>
        </div>
      )}

      {/* STEP 2: Loading */}
      {step === 2 && (
        <div style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '48px', textAlign: 'center' }}>
          <div style={{ width: '48px', height: '48px', border: '4px solid #e2e8f0', borderTopColor: '#0369a1', borderRadius: '50%', margin: '0 auto 24px', animation: 'spin 1s linear infinite' }} />
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          <h2 style={{ margin: '0 0 8px' }}>Performing FTO Analysis</h2>
          <p style={{ color: '#64748b' }}>{progress.message}</p>
          <div style={{ background: '#f1f5f9', borderRadius: '8px', height: '8px', maxWidth: '400px', margin: '16px auto' }}>
            <div style={{ background: '#0369a1', height: '100%', width: `${progress.percent}%`, borderRadius: '8px', transition: 'width 0.5s' }} />
          </div>
          <p style={{ color: '#94a3b8', fontSize: '14px' }}>{progress.percent}% complete</p>
        </div>
      )}

      {/* STEP 3: Results */}
      {step === 3 && results && (
        <div>
          {/* Risk Banner */}
          <div style={{ background: getRiskColor(results.executive_summary.overall_risk).bg, border: `2px solid ${getRiskColor(results.executive_summary.overall_risk).border}`, borderRadius: '12px', padding: '24px', marginBottom: '24px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px' }}>
              <div>
                <div style={{ fontSize: '14px', color: getRiskColor(results.executive_summary.overall_risk).text }}>Overall Risk Assessment</div>
                <div style={{ fontSize: '28px', fontWeight: '700', color: getRiskColor(results.executive_summary.overall_risk).text }}>{results.executive_summary.overall_risk}</div>
                <span style={{ display: 'inline-block', marginTop: '8px', padding: '6px 16px', background: '#d97706', color: 'white', borderRadius: '16px', fontWeight: '600' }}>{results.executive_summary.go_no_go}</span>
              </div>
              <div style={{ display: 'flex', gap: '12px' }}>
                <div style={{ background: 'white', padding: '12px 20px', borderRadius: '8px', textAlign: 'center' }}><div style={{ fontSize: '24px', fontWeight: '700', color: '#dc2626' }}>{results.high_risk_patents.length}</div><div style={{ fontSize: '11px', color: '#64748b' }}>HIGH</div></div>
                <div style={{ background: 'white', padding: '12px 20px', borderRadius: '8px', textAlign: 'center' }}><div style={{ fontSize: '24px', fontWeight: '700', color: '#d97706' }}>{results.medium_risk_patents.length}</div><div style={{ fontSize: '11px', color: '#64748b' }}>MED</div></div>
                <div style={{ background: 'white', padding: '12px 20px', borderRadius: '8px', textAlign: 'center' }}><div style={{ fontSize: '24px', fontWeight: '700', color: '#16a34a' }}>{results.low_risk_patents.length}</div><div style={{ fontSize: '11px', color: '#64748b' }}>LOW</div></div>
              </div>
            </div>
            <p style={{ marginTop: '16px', marginBottom: 0, color: getRiskColor(results.executive_summary.overall_risk).text }}>{results.executive_summary.headline}</p>
          </div>

          {/* Disclaimer */}
          <div style={{ background: '#fef3c7', borderLeft: '4px solid #f59e0b', padding: '12px 16px', marginBottom: '24px', borderRadius: '0 8px 8px 0', fontSize: '13px', color: '#92400e' }}>
            <strong>Disclaimer:</strong> This AI-assisted analysis is for informational purposes only. Review with qualified patent counsel before making business decisions.
          </div>

          {/* 1. Executive Summary */}
          <Section title="1. Executive Summary" expanded={expandedSections.executive} onToggle={() => toggleSection('executive')}>
            <h4 style={{ margin: '0 0 12px' }}>Key Findings</h4>
            <ul style={{ margin: '0 0 16px', paddingLeft: '20px' }}>{results.executive_summary.key_findings.map((f, i) => <li key={i} style={{ marginBottom: '6px' }}>{f}</li>)}</ul>
            <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: '200px', background: '#f8fafc', padding: '12px', borderRadius: '8px' }}><div style={{ fontSize: '12px', color: '#64748b' }}>Estimated Clearance Cost</div><div style={{ fontWeight: '600' }}>{results.executive_summary.estimated_clearance_cost}</div></div>
              <div style={{ flex: 1, minWidth: '200px', background: '#f8fafc', padding: '12px', borderRadius: '8px' }}><div style={{ fontSize: '12px', color: '#64748b' }}>Timeline Impact</div><div style={{ fontWeight: '600' }}>{results.executive_summary.timeline_impact}</div></div>
            </div>
          </Section>

          {/* 2. Heat Map */}
          <Section title="2. Feature Risk Heat Map" expanded={expandedSections.heatmap} onToggle={() => toggleSection('heatmap')}>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                <thead><tr style={{ background: '#f1f5f9' }}><th style={{ padding: '10px', textAlign: 'left', borderBottom: '1px solid #e2e8f0' }}>Feature</th><th style={{ padding: '10px', textAlign: 'center', borderBottom: '1px solid #e2e8f0', width: '80px' }}>Risk</th><th style={{ padding: '10px', textAlign: 'left', borderBottom: '1px solid #e2e8f0' }}>Notes</th></tr></thead>
                <tbody>{results.heat_map.features.map((f, i) => (
                  <tr key={i}><td style={{ padding: '10px', borderBottom: '1px solid #e2e8f0' }}>{f.name}</td><td style={{ padding: '10px', borderBottom: '1px solid #e2e8f0', textAlign: 'center' }}><span style={{ padding: '3px 10px', borderRadius: '10px', fontSize: '12px', fontWeight: '600', background: getRiskColor(f.risk).bg, color: getRiskColor(f.risk).text }}>{f.risk}</span></td><td style={{ padding: '10px', borderBottom: '1px solid #e2e8f0', color: '#64748b' }}>{f.notes}</td></tr>
                ))}</tbody>
              </table>
            </div>
          </Section>

          {/* 3. HIGH Risk Patents */}
          <Section title={`3. HIGH Risk Patents (${results.high_risk_patents.length})`} expanded={expandedSections['high-risk']} onToggle={() => toggleSection('high-risk')} badge={results.high_risk_patents.length} badgeColor="#fee2e2" badgeText="#991b1b">
            {results.high_risk_patents.map((p, i) => (
              <div key={i} style={{ border: '1px solid #fecaca', borderLeft: '4px solid #dc2626', borderRadius: '8px', padding: '16px', marginBottom: '12px', background: '#fef2f2' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px', flexWrap: 'wrap', gap: '8px' }}>
                  <a href={`https://patents.google.com/patent/US${p.patent_number}`} target="_blank" rel="noopener noreferrer" style={{ color: '#0369a1', fontWeight: '600', fontSize: '15px' }}>US{p.patent_number}</a>
                  <span style={{ background: '#dc2626', color: 'white', padding: '2px 10px', borderRadius: '10px', fontSize: '12px' }}>{p.relevance_score}% Match</span>
                </div>
                <div style={{ fontWeight: '500', marginBottom: '8px' }}>{p.title}</div>
                <div style={{ fontSize: '13px', color: '#64748b', marginBottom: '8px' }}>Assignee: {p.assignee} | Expires: {p.expiry_date}</div>
                <div style={{ fontSize: '13px', marginBottom: '6px' }}><strong>Key Claims:</strong> {p.key_claims.join('; ')}</div>
                <div style={{ fontSize: '13px', marginBottom: '6px' }}><strong>Overlap:</strong> {p.overlap_analysis}</div>
                <div style={{ fontSize: '13px', marginBottom: '6px' }}><strong>DoE Sensitivity:</strong> {p.doe_sensitivity}</div>
                <div style={{ fontSize: '13px', marginBottom: '6px' }}><strong>Design-Around:</strong> {p.design_around_difficulty}</div>
                <div style={{ background: 'white', padding: '10px', borderRadius: '6px', fontSize: '13px', marginTop: '8px' }}><strong>⚠️ Counsel Note:</strong> {p.counsel_note}</div>
              </div>
            ))}
          </Section>

          {/* 4. MEDIUM Risk Patents */}
          <Section title={`4. MEDIUM Risk Patents (${results.medium_risk_patents.length})`} expanded={expandedSections['medium-risk']} onToggle={() => toggleSection('medium-risk')}>
            {results.medium_risk_patents.map((p, i) => (
              <div key={i} style={{ border: '1px solid #fde68a', borderLeft: '4px solid #d97706', borderRadius: '8px', padding: '16px', marginBottom: '12px', background: '#fffbeb' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <a href={`https://patents.google.com/patent/US${p.patent_number}`} target="_blank" rel="noopener noreferrer" style={{ color: '#0369a1', fontWeight: '600' }}>US{p.patent_number}: {p.title}</a>
                  <span style={{ background: '#d97706', color: 'white', padding: '2px 10px', borderRadius: '10px', fontSize: '12px' }}>{p.relevance_score}%</span>
                </div>
                <div style={{ fontSize: '13px', color: '#64748b', marginBottom: '6px' }}>Assignee: {p.assignee} | Expires: {p.expiry_date}</div>
                <div style={{ fontSize: '13px', marginBottom: '6px' }}>{p.overlap_analysis}</div>
                <div style={{ fontSize: '13px', color: '#92400e' }}><strong>Counsel:</strong> {p.counsel_note}</div>
              </div>
            ))}
          </Section>

          {/* 5. LOW Risk Patents */}
          <Section title={`5. LOW Risk Patents (${results.low_risk_patents.length})`} expanded={expandedSections['low-risk']} onToggle={() => toggleSection('low-risk')}>
            {results.low_risk_patents.map((p, i) => (
              <div key={i} style={{ border: '1px solid #bbf7d0', borderRadius: '8px', padding: '12px', marginBottom: '8px', background: '#f0fdf4', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
                <div><a href={`https://patents.google.com/patent/US${p.patent_number}`} target="_blank" rel="noopener noreferrer" style={{ color: '#0369a1', fontWeight: '500' }}>US{p.patent_number}</a><span style={{ marginLeft: '8px', color: '#64748b' }}>{p.title}</span></div>
                <span style={{ color: '#166534', fontSize: '13px' }}>{p.counsel_note}</span>
              </div>
            ))}
          </Section>

          {/* 6. Design-Around Strategies */}
          <Section title="6. Design-Around Strategies" expanded={expandedSections['design-around']} onToggle={() => toggleSection('design-around')}>
            {results.design_around_options.map((d, i) => (
              <div key={i} style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderLeft: `4px solid ${d.recommendation === 'PREFERRED' ? '#16a34a' : '#d97706'}`, borderRadius: '8px', padding: '16px', marginBottom: '12px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', flexWrap: 'wrap', gap: '8px' }}>
                  <strong>{d.id}: {d.strategy}</strong>
                  <span style={{ background: d.recommendation === 'PREFERRED' ? '#16a34a' : '#d97706', color: 'white', padding: '2px 10px', borderRadius: '10px', fontSize: '11px' }}>{d.recommendation}</span>
                </div>
                <div style={{ fontSize: '13px', color: '#64748b', marginBottom: '6px' }}>Target: {d.target_patent}</div>
                <div style={{ fontSize: '14px', marginBottom: '10px' }}>{d.description}</div>
                <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', fontSize: '13px' }}>
                  <span><strong>Effort:</strong> {d.implementation_effort}</span>
                  <span><strong>Timeline:</strong> {d.timeline}</span>
                  <span><strong>Cost:</strong> {d.cost_estimate}</span>
                  <span><strong>Impact:</strong> {d.performance_impact}</span>
                </div>
              </div>
            ))}
          </Section>

          {/* 7. Prior Art / Academic Papers */}
          <Section title="7. Prior Art (Validity Challenges)" expanded={expandedSections['prior-art']} onToggle={() => toggleSection('prior-art')}>
            <p style={{ fontSize: '14px', color: '#64748b', marginBottom: '16px' }}>Academic papers that may support invalidity arguments. <em>Note: Validity analysis is separate from infringement (validity firewall).</em></p>
            {results.academic_papers.map((p, i) => (
              <div key={i} style={{ border: '1px solid #e2e8f0', borderRadius: '8px', padding: '16px', marginBottom: '12px', background: '#fafafa' }}>
                <div style={{ fontWeight: '600', marginBottom: '6px' }}>{p.title}</div>
                <div style={{ fontSize: '13px', color: '#64748b', marginBottom: '6px' }}>{p.authors} | {p.venue} ({p.year}) | {p.cited_by_count} citations</div>
                <div style={{ fontSize: '13px', marginBottom: '8px' }}><strong>Relevance:</strong> {p.relevance}</div>
                <a href={`https://doi.org/${p.doi}`} target="_blank" rel="noopener noreferrer" style={{ fontSize: '13px', color: '#0369a1' }}>View Paper →</a>
              </div>
            ))}
          </Section>

          {/* 8. Competitor Analysis */}
          <Section title="8. Competitor Portfolio Analysis" expanded={expandedSections.competitors} onToggle={() => toggleSection('competitors')}>
            {results.competitor_archetypes.map((c, i) => (
              <div key={i} style={{ border: '1px solid #e2e8f0', borderRadius: '8px', padding: '16px', marginBottom: '12px', background: 'white' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px', flexWrap: 'wrap', gap: '8px' }}>
                  <strong style={{ fontSize: '15px' }}>{c.name}</strong>
                  <span style={{ padding: '3px 10px', borderRadius: '10px', fontSize: '11px', fontWeight: '600', background: c.risk_profile.includes('HIGH') ? '#fee2e2' : '#fef3c7', color: c.risk_profile.includes('HIGH') ? '#991b1b' : '#92400e' }}>{c.risk_profile}</span>
                </div>
                <p style={{ fontSize: '14px', margin: '0 0 8px' }}>{c.strategy}</p>
                <div style={{ fontSize: '13px', color: '#64748b', marginBottom: '6px' }}>Patents: {c.patent_count} | Key: {c.key_patents.join(', ')}</div>
                <div style={{ fontSize: '13px', color: '#64748b' }}><em>Licensing: {c.licensing_history}</em></div>
              </div>
            ))}
          </Section>

          {/* 9. Attorney Brief */}
          <Section title="9. Attorney Brief" expanded={expandedSections.attorney} onToggle={() => toggleSection('attorney')}>
            <h4 style={{ margin: '0 0 12px' }}>Priority Actions</h4>
            <ol style={{ margin: '0 0 16px', paddingLeft: '20px' }}>{results.attorney_brief.priority_actions.map((a, i) => <li key={i} style={{ marginBottom: '6px' }}>{a}</li>)}</ol>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '12px' }}>
              <div style={{ background: '#f8fafc', padding: '12px', borderRadius: '8px' }}><div style={{ fontSize: '12px', color: '#64748b' }}>Claim Charts Needed</div><div style={{ fontSize: '14px' }}>{results.attorney_brief.claim_chart_needed.join(', ')}</div></div>
              <div style={{ background: '#f8fafc', padding: '12px', borderRadius: '8px' }}><div style={{ fontSize: '12px', color: '#64748b' }}>Estimated Legal Cost</div><div style={{ fontSize: '14px', fontWeight: '600' }}>{results.attorney_brief.estimated_legal_cost}</div></div>
            </div>
            <div style={{ background: '#f8fafc', padding: '12px', borderRadius: '8px', marginTop: '12px' }}><div style={{ fontSize: '12px', color: '#64748b' }}>Opinion Letter Scope</div><div style={{ fontSize: '14px' }}>{results.attorney_brief.opinion_letter_scope}</div></div>
          </Section>

          {/* 10. Conclusions */}
          <Section title="10. Conclusions & Recommendations" expanded={expandedSections.conclusions} onToggle={() => toggleSection('conclusions')}>
            <p style={{ marginBottom: '16px' }}>{results.conclusions.overall_assessment}</p>
            <h4 style={{ margin: '0 0 8px' }}>Conditions for Proceeding</h4>
            <ul style={{ margin: '0 0 16px', paddingLeft: '20px' }}>{results.conclusions.conditions.map((c, i) => <li key={i} style={{ marginBottom: '4px' }}>{c}</li>)}</ul>
            <h4 style={{ margin: '0 0 8px' }}>Escalation Triggers</h4>
            <ul style={{ margin: '0 0 16px', paddingLeft: '20px' }}>{results.conclusions.escalation_triggers.map((t, i) => <li key={i} style={{ marginBottom: '4px' }}>{t}</li>)}</ul>
            <h4 style={{ margin: '0 0 8px' }}>Change Triggers</h4>
            <ul style={{ margin: 0, paddingLeft: '20px' }}>{results.change_triggers.map((t, i) => <li key={i} style={{ marginBottom: '4px' }}>{t}</li>)}</ul>
          </Section>

          {/* 11. Verification Status */}
          <Section title="11. Verification Status & Limitations" expanded={expandedSections.verification} onToggle={() => toggleSection('verification')}>
            <h4 style={{ margin: '0 0 12px' }}>Verification Status</h4>
            <div style={{ overflowX: 'auto', marginBottom: '20px' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                <thead><tr style={{ background: '#f1f5f9' }}><th style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid #e2e8f0' }}>Item</th><th style={{ padding: '8px', textAlign: 'center', borderBottom: '1px solid #e2e8f0' }}>Status</th><th style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid #e2e8f0' }}>Source</th><th style={{ padding: '8px', textAlign: 'center', borderBottom: '1px solid #e2e8f0' }}>Confidence</th></tr></thead>
                <tbody>{Object.entries(results.verification_status).map(([k, v]) => (
                  <tr key={k}><td style={{ padding: '8px', borderBottom: '1px solid #e2e8f0', textTransform: 'capitalize' }}>{k.replace(/_/g, ' ')}</td><td style={{ padding: '8px', borderBottom: '1px solid #e2e8f0', textAlign: 'center' }}><span style={{ padding: '2px 8px', borderRadius: '4px', fontSize: '11px', background: v.status === 'verified' ? '#dcfce7' : v.status === 'calculated' ? '#e0f2fe' : v.status === 'as_filed' ? '#fef3c7' : '#fee2e2', color: v.status === 'verified' ? '#166534' : v.status === 'calculated' ? '#0369a1' : v.status === 'as_filed' ? '#92400e' : '#991b1b' }}>{v.status}</span></td><td style={{ padding: '8px', borderBottom: '1px solid #e2e8f0', color: '#64748b' }}>{v.source}</td><td style={{ padding: '8px', borderBottom: '1px solid #e2e8f0', textAlign: 'center' }}>{v.confidence || '—'}</td></tr>
                ))}</tbody>
              </table>
            </div>
            <h4 style={{ margin: '0 0 12px' }}>Database Limitations</h4>
            <div style={{ background: '#f8fafc', padding: '16px', borderRadius: '8px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px', marginBottom: '12px', fontSize: '14px' }}>
                <div><strong>Patents:</strong> {results.database_limitations.patents_included}</div>
                <div><strong>Date Range:</strong> {results.database_limitations.date_range}</div>
                <div><strong>Applications:</strong> {results.database_limitations.applications_included ? 'Included' : 'Not included'}</div>
                <div><strong>Non-US:</strong> {results.database_limitations.non_us_patents ? 'Included' : 'Not included'}</div>
              </div>
              <div style={{ fontSize: '13px', color: '#64748b' }}><strong>Impact:</strong><ul style={{ margin: '8px 0 0', paddingLeft: '20px' }}>{results.database_limitations.impact_notes.map((n, i) => <li key={i}>{n}</li>)}</ul></div>
            </div>
          </Section>

          {/* Action Buttons */}
          <div style={{ display: 'flex', gap: '12px', marginTop: '24px', flexWrap: 'wrap' }}>
            <button onClick={resetAnalysis} style={{ padding: '12px 24px', background: 'white', color: '#0369a1', border: '1px solid #0369a1', borderRadius: '8px', cursor: 'pointer', fontWeight: '600' }}>← New Analysis</button>
            <button onClick={saveReport} disabled={saving} style={{ padding: '12px 24px', background: saveSuccess ? '#16a34a' : '#0369a1', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '500' }}>{saving ? 'Saving...' : saveSuccess ? '✓ Saved!' : 'Save Report'}</button>
            <button onClick={downloadHTML} style={{ padding: '12px 24px', background: 'white', color: '#374151', border: '1px solid #d1d5db', borderRadius: '8px', cursor: 'pointer' }}>Download HTML</button>
            <button onClick={downloadPDF} style={{ padding: '12px 24px', background: '#0369a1', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '500' }}>Download PDF</button>
          </div>
        </div>
      )}
    </div>
  );
};

// Collapsible Section Component
const Section = ({ title, expanded, onToggle, children }) => (
  <div style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: '12px', marginBottom: '16px', overflow: 'hidden' }}>
    <button onClick={onToggle} style={{ width: '100%', padding: '16px 20px', background: expanded ? '#f8fafc' : 'white', border: 'none', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', textAlign: 'left' }}>
      <span style={{ fontSize: '16px', fontWeight: '600', color: '#1e293b' }}>{title}</span>
      <span style={{ color: '#64748b', fontSize: '12px' }}>{expanded ? '▼' : '▶'}</span>
    </button>
    {expanded && <div style={{ padding: '0 20px 20px' }}>{children}</div>}
  </div>
);

export default FTOProPage;